import { createApp } from 'vue';
import Antd from 'ant-design-vue';
import VueGtag from "vue-gtag";
import { Base64 } from 'js-base64';
import DOMPurify from 'dompurify';

import App from './App.vue';
import dayjs from '@/helpers/dayjstz';
import store from '@/store';
import router from '@/router';
import * as Sentry from '@sentry/vue';
import MultiEventHandler from '@/services/MultiEventHandler';
import CacheService from '@/services/Cache';
import webMcp from '@/services/WebMcp';
import logRocket from '@/plugins/log-rocket';
import dateAdapter from '@/helpers/dayjsAdapter';
import { areToasterErrorsSuppressed } from '@/helpers/toasterSuppression';
import { TOO_COMPLEX_ERROR_CODE } from '@/helpers/tooComplexError';
import {
    REPLIES_LIMIT_ERROR_CODE,
    dismissRepliesLimitToast,
    isRepliesLimitToastDismissed,
    isStaleRepliesLimitError
} from '@/helpers/repliesLimitError';
import { BrowserTracing } from '@sentry/tracing';
import DateHandlerService from '@/services/DateHandler';
import PageHeader from '@/components/global/PageHeader';
import SocialMediaHandlerService from '@/services/SocialMediaHandler';
import MixpanelEventsHandler from '@/services/mixpanel/MixpanelEventsHandler';
import SkeletonLoader from '@/components/global/SkeletonLoader';
import { environment, apiUrl, isApiMocked, sentry as sentryConfig } from '@/config';
import { PROD } from '@/assets/common/environment';
import GetValidationErrorMessageHandler from '@/services/GetValidationErrorMessageHandler';

require('@/bootstrap/axios.config');
require('@/bootstrap/global-styles');
import 'katex/dist/katex.min.css';

if (isApiMocked) {
    require('@/bootstrap/mock');
}

const app = createApp(App);

const cacheService = new CacheService();

cacheService.cleanLocalStorageFromExpiredKeys();

Sentry.init({
    app,
    ...sentryConfig,
    integrations: [
        new BrowserTracing({
            routingInstrumentation: Sentry.vueRouterInstrumentation(router),
            tracingOrigins: [apiUrl, /^\//]
        })
    ]
});

app.use(router);
app.use(store);
app.use(Antd);
if (environment === PROD) {
    app.use(VueGtag, { config: { id: "G-78GZJG9VGE" } }, router);
}

app.component('PageHeader', PageHeader);
app.component('SkeletonLoader', SkeletonLoader);

app.directive('check-avatar-image', {
    created(el, { value, arg: backgroundColor }) {
        if (!value) {
            return;
        }

        const img = new Image();

        img.onload = () => {
            el.append(img);

            const [span] = el.children;
            span.remove();
        };

        img.onerror = () => {
            if (backgroundColor) {
                el.style.backgroundColor = backgroundColor;
            }

            console.warn(`We are not able to fetch image: ${value}`);
        };

        img.src = value;
    }
});

// @DEPRECATED (use dateAdapter instead and remove that assignment)
app.config.globalProperties.$dayjs = dayjs;

app.config.globalProperties.$toaster = function (message, type = 'success', options = {}) {
    this.$message[type]({ content: message, ...options });
};
app.config.globalProperties.$toasterError = function (message = null, error = null) {
    if (areToasterErrorsSuppressed()) {
        return;
    }

    if (error?.isTooComplex) {
        this.$message.error({ content: error.message, key: TOO_COMPLEX_ERROR_CODE, duration: 10 });
    } else if (error?.isRepliesLimit) {
        // a late answer for a range or search the user has already left, or one whose toast they already closed
        if (isStaleRepliesLimitError(error) || isRepliesLimitToastDismissed()) {
            return;
        }

        this.$message.error({
            content: error.message,
            key: REPLIES_LIMIT_ERROR_CODE,
            duration: 0,
            onClick: () => {
                dismissRepliesLimitToast();
                this.$message.destroy(REPLIES_LIMIT_ERROR_CODE);
            }
        });
    } else {
        this.$message.error(message || 'Something went wrong');
    }
};

app.config.globalProperties.$getValidationErrorMessageHandler = new GetValidationErrorMessageHandler();

app.config.globalProperties.$filters = {
    formatDate: value => {
        if (value) {
            return dayjs(value).format('DD MMM YYYY');
        }

        return '-';
    },

    formatDateAndTime: value => {
        if (value) {
            return dayjs(value).format('DD MMM YYYY HH:mm:ss');
        }

        return '-';
    }
};

app.config.globalProperties.$sendAnalytics = (eventName, payload = {}, id = '') => {
    if (window.analytics) {
        window.analytics[id ? 'identify' : 'track'](id || eventName, payload);
    }

    if (logRocket?._isInitialized) {
        logRocket.identify(eventName, payload);
    }
};

app.config.globalProperties.$mixpanelEvents = new MixpanelEventsHandler();

app.config.globalProperties.$multiEventHandler = new MultiEventHandler();

app.config.globalProperties.$cacheService = cacheService;

app.config.globalProperties.$webMcp = webMcp;

app.config.globalProperties.$dateAdapter = dateAdapter;

app.config.globalProperties.$dateService = new DateHandlerService(dateAdapter);

app.config.globalProperties.$socialMediaService = new SocialMediaHandlerService();

app.config.globalProperties.$logRocket = logRocket

app.config.globalProperties.$sanitize = DOMPurify.sanitize;

app.config.globalProperties.$base64 = Base64;

const { dispatch } = store;

dispatch('externalScripts/initSegment');

router.isReady().then(() => {
    app.mount('#app');
});

